import { useMemo, useState } from 'react';
import { Button, Checkbox, Divider, Input, Popover, Typography, message, theme as antdTheme } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { typeScale, space, tracking } from '@viq-admin/ui';
import { brandColors } from '@viq-admin/theme';
import {
  STATUS_VALUES,
  type BatchTagsApi,
  type CustomTagDefinition,
  type LeadTag,
  type TagCategory,
} from './types';

interface Props {
  personId: string;
  currentTags: LeadTag[];
  customTagDefinitions: CustomTagDefinition[];
  tagsApi: BatchTagsApi;
  onTagsChange: (tags: LeadTag[]) => void;
}

function isSelected(tags: LeadTag[], category: TagCategory, value: string): boolean {
  return tags.some((t) => t.category === category && t.value === value);
}

/**
 * Multi-select tag picker. Click ⊕ → popover with VIP, Status, and Custom
 * sections. Each toggle fires an optimistic add/remove, calls the shell-provided
 * `tagsApi`, and rolls back the row on failure with a toast. Ported from the
 * portal's `src/views/BatchScreen/TagPicker.tsx`; the only change is that the
 * add/remove endpoints arrive via `tagsApi` instead of a direct module import.
 */
export function TagPicker({
  personId,
  currentTags,
  customTagDefinitions,
  tagsApi,
  onTagsChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [newCustom, setNewCustom] = useState('');
  const { token } = antdTheme.useToken();

  const customValues = useMemo(
    () => customTagDefinitions.map((d) => d.value),
    [customTagDefinitions],
  );

  async function toggleTag(category: TagCategory, value: string) {
    const already = isSelected(currentTags, category, value);
    const previous = currentTags;
    const optimistic: LeadTag[] = already
      ? currentTags.filter((t) => !(t.category === category && t.value === value))
      : [...currentTags, { category, value }];

    // Apply optimistically first.
    onTagsChange(optimistic);

    try {
      const server = already
        ? await tagsApi.removeTag(personId, category, value)
        : await tagsApi.addTag(personId, { category, value });
      onTagsChange(server.tags);
    } catch (err) {
      // Rollback to the previous state + toast.
      onTagsChange(previous);
      void message.error(
        already
          ? 'Couldn’t remove the tag. Try again.'
          : 'Couldn’t add the tag. Try again.',
      );
      // eslint-disable-next-line no-console
      console.error('tag toggle failed', err);
    }
  }

  async function addCustom() {
    const v = newCustom.trim();
    if (!v) return;
    setNewCustom('');
    await toggleTag('custom', v);
  }

  const vipSelected = isSelected(currentTags, 'vip', 'vip');

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      content={
        <div style={{ width: 240 }}>
          <Typography.Text strong style={{ fontSize: typeScale.caption, letterSpacing: tracking.label }}>
            VIP
          </Typography.Text>
          <div style={{ margin: '8px 0' }}>
            <Checkbox
              checked={vipSelected}
              onChange={() => void toggleTag('vip', 'vip')}
            >
              <span style={{ color: brandColors.signatureGold, fontWeight: 600 }}>
                ★ VIP
              </span>
            </Checkbox>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          <Typography.Text strong style={{ fontSize: typeScale.caption, letterSpacing: tracking.label }}>
            STATUS
          </Typography.Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.sm }}>
            {STATUS_VALUES.map((v) => (
              <Checkbox
                key={v}
                checked={isSelected(currentTags, 'status', v)}
                onChange={() => void toggleTag('status', v)}
              >
                {v}
              </Checkbox>
            ))}
          </div>

          <Divider style={{ margin: '8px 0' }} />

          <Typography.Text strong style={{ fontSize: typeScale.caption, letterSpacing: tracking.label }}>
            CUSTOM
          </Typography.Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.sm }}>
            {customValues.map((v) => (
              <Checkbox
                key={v}
                checked={isSelected(currentTags, 'custom', v)}
                onChange={() => void toggleTag('custom', v)}
              >
                {v}
              </Checkbox>
            ))}
          </div>

          <div style={{ display: 'flex', gap: space.xs, marginTop: space.sm }}>
            <Input
              size="small"
              placeholder="New tag"
              value={newCustom}
              onChange={(e) => setNewCustom(e.target.value)}
              onPressEnter={() => void addCustom()}
              maxLength={64}
            />
            <Button size="small" type="primary" onClick={() => void addCustom()}>
              Add
            </Button>
          </div>
        </div>
      }
    >
      <Button
        size="small"
        type="text"
        aria-label="Add tag"
        icon={<PlusOutlined />}
        style={{ color: token.colorTextQuaternary }}
      />
    </Popover>
  );
}
